import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const tiers = [
    {
      name: 'Foundation',
      description: '1–5 data sources',
      target: 'For mid-size companies starting their data observability journey',
      highlighted: false,
      features: [
        'Core observers (schema, volume, freshness)',
        'Explorer & data source browsing',
        'Spaces for domain organization',
        'Basic alerting (email)',
        'Standard support'
      ]
    },
    {
      name: 'Professional',
      description: '5–20 data sources',
      target: 'For enterprises with complex data infrastructure and BI dependencies',
      highlighted: true,
      features: [
        'Everything in Foundation, plus:',
        'BI Discovery — map dashboards to upstream data',
        'Drift Detection (ML-powered)',
        'AI-powered anomaly suggestions',
        'Advanced alerting (Slack, email, webhooks)',
        'Priority support with faster SLAs'
      ]
    },
    {
      name: 'Enterprise',
      description: '20+ data sources',
      target: 'For large enterprises and regulated industries at scale',
      highlighted: false,
      features: [
        'Everything in Professional, plus:',
        'Custom observer development',
        'Dedicated onboarding program',
        'Priority support with dedicated account team',
        'Enterprise security & compliance review',
        'Multi-environment support (dev/staging/prod)'
      ]
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl font-bold mb-4 text-gray-900">
            Simple, value-based pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Annual licenses with on-premise deployment. Every plan includes
            full data sovereignty — your data never leaves your infrastructure.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              className={`rounded-2xl overflow-hidden flex flex-col h-full ${
                tier.highlighted
                  ? 'border-2 border-purple-500 shadow-lg shadow-purple-100'
                  : 'border border-gray-200 shadow-sm'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {tier.highlighted && (
                <div className="bg-purple-600 text-white text-center py-2 text-sm font-medium">
                  Most Popular
                </div>
              )}
              <div className="p-8 flex flex-col flex-grow">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">{tier.name}</h2>
                <p className="text-sm text-purple-600 font-medium mb-3">{tier.description}</p>
                <p className="text-sm text-gray-500 mb-6">{tier.target}</p>

                <Link
                  to="/contact"
                  className={`block w-full py-3 px-4 text-center rounded-lg transition-colors mb-8 font-medium ${
                    tier.highlighted
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Contact Sales
                </Link>

                <ul className="space-y-3 flex-grow">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      {i === 0 && feature.startsWith('Everything') ? (
                        <span className="text-sm text-gray-500 font-medium">{feature}</span>
                      ) : (
                        <>
                          <svg className="h-5 w-5 text-green-500 mr-2.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-gray-600">{feature}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Professional Services */}
        <motion.div
          className="bg-gray-50 rounded-2xl p-8 lg:p-10 border border-gray-100 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Professional Services</h2>
              <p className="text-gray-600 mb-4">
                Every Qupid deployment includes a structured onboarding program led by
                our engineering team — the same people who built the product.
              </p>
              <ul className="space-y-2">
                {[
                  'On-premise deployment and configuration (4–8 weeks)',
                  'Data source integration and observer setup',
                  'Team training and knowledge transfer',
                  'Ongoing technical support and optimization'
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-sm text-gray-600">
                    <svg className="h-4 w-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center lg:text-right">
              <p className="text-sm text-gray-500 mb-2">Professional services are priced separately</p>
              <Link to="/contact" className="button button-primary inline-block">
                Discuss Your Deployment
              </Link>
            </div>
          </div>
        </motion.div>

        {/* On-premise badge */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-900 text-white rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-sm font-medium">All plans include on-premise deployment — your data never leaves your infrastructure</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
